import React from 'react';
import CodeBlock from '../../CodeBlock';
import { Network, Layers } from 'lucide-react';

const CorpusMapWorkflow = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <div className="workflow-section">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-white">Structure Exploration</h1>
        <p className="text-lg md:text-xl text-gray-300 leading-7 max-w-[65ch]">
          Explore the structure of your dataset by generating corpus maps. Corpus maps provide an ontology and taxonomy
          extracted from your documents, giving you insights into the key concepts and relationships in your data.
        </p>
      </div>

      <div className="card mb-6 bg-gray-900 border border-gray-800">
        <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">What are corpus maps?</h3>
        <p className="text-sm text-gray-300 leading-6 mb-4">
          Corpus maps are high-level visualizations of your dataset that show:
        </p>
        <ul className="space-y-2 text-sm text-gray-300 list-disc list-inside ml-2">
          <li>The main topics and themes in your documents</li>
          <li>How concepts relate to each other</li>
          <li>The overall structure and organization of your knowledge</li>
        </ul>
        <p className="text-sm text-gray-400 mt-4 leading-6">
          Think of them as a "map" of your dataset—they help you quickly understand what your documents contain and how
          different pieces of information connect.
        </p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Prerequisites */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">1</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Ensure documents are processed</h3>
              <p className="text-sm text-gray-400 leading-6">
                You need a namespace with documents that have completed ingestion. The corpus map is generated from the
                entities and relationships extracted during document processing.
              </p>
            </div>
          </div>

          <div className="ml-14">
            <p className="text-sm text-gray-300 mb-2 leading-6">
              Verify your documents have <code className="text-green-400 bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code> ingestion status:
            </p>
            <CodeBlock code={`curl -X GET ${BASE_URL}/ingestionTasks/{ingestion_task_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
          </div>
        </div>

        {/* Step 2: Generate Corpus Map */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">2</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Generate a corpus map</h3>
              <p className="text-sm text-gray-400 leading-6">
                Create a corpus map from your namespace. The API analyzes the entities and relationships in your documents
                to generate an ontology and taxonomy that represents your dataset structure.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "generate_ontology": true,
    "generate_taxonomy": true
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "namespace_uuid": "{namespace_uuid}",
  "status": "PENDING",
  "generate_ontology": true,
  "generate_taxonomy": true,
  "options": null,
  "created_at": "2024-01-15T11:00:00Z",
  "updated_at": "2024-01-15T11:00:00Z"
}`} />
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-200 flex items-start gap-2">
                <Layers className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Generation process:</strong> Corpus map generation analyzes your documents'
                  knowledge graph to identify key concepts, their relationships, and overall structure. This typically takes 1-3 minutes.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 3: Retrieve Corpus Map */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">3</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Retrieve your corpus map</h3>
              <p className="text-sm text-gray-400 leading-6">
                Once generation is complete, retrieve the corpus map to see the ontology, taxonomy, and key insights about your dataset.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-3">Check task status</h4>
              <p className="text-sm text-gray-400 mb-2 leading-6">
                Poll the task endpoint to monitor generation progress. The status will change from <code className="text-blue-400 bg-gray-800 px-1 py-0.5 rounded">PENDING</code> to <code className="text-blue-400 bg-gray-800 px-1 py-0.5 rounded">PROCESSING</code> to <code className="text-green-400 bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code>.
              </p>
              <CodeBlock code={`curl -X GET ${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps/{task_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (Processing)</h4>
              <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "namespace_uuid": "{namespace_uuid}",
  "status": "PROCESSING",
  "generate_ontology": true,
  "generate_taxonomy": true,
  "progress": {
    "stage": "PROCESSING",
    "message": "Generating corpus map from knowledge graph..."
  },
  "result": null,
  "created_at": "2024-01-15T11:00:00Z",
  "updated_at": "2024-01-15T11:01:00Z"
}`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-3 mt-4">Retrieve results</h4>
              <p className="text-sm text-gray-400 mb-2 leading-6">
                Once the task status is <code className="text-green-400 bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code>,
                retrieve the full corpus map results using the <code className="text-blue-400 bg-gray-800 px-1 py-0.5 rounded">/results</code> endpoint.
              </p>
              <CodeBlock code={`curl -X GET ${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps/{task_uuid}/results?include_ontology=true&include_taxonomy=true \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (Results)</h4>
              <CodeBlock code={`{
  "namespace_uuid": "{namespace_uuid}",
  "generated_at": "2024-01-15T11:02:30Z",
  "statistics": {
    "total_entities": 109,
    "total_relationships": 134,
    "ontology_generated": true,
    "taxonomy_generated": true
  },
  "ontology": {
    "entities": [...],
    "relationships": [...],
    "entity_type_counts": {
      "PROCESS": 38,
      "PROBLEM": 20,
      "CONCEPT": 13
    },
    "relationship_type_counts": {
      "RELATED_TO": 43,
      "AFFECTS": 28
    }
  },
  "taxonomy": {
    "hierarchical_structure": [
      {
        "id": "category_1",
        "name": "Customer Support Management",
        "level": 1,
        "description": "This category encompasses all aspects of customer support management.",
        "children": [...]
      }
    ],
    "categories": ["Customer Support Management"],
    "levels": 3
  },
  "summary": "=== CORPUS DOMAIN OVERVIEW ===\\n\\nTotal Entities: 109\\nTotal Relationships: 134\\n..."
}`} />
            </div>

            <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
              <p className="text-sm text-green-200 flex items-start gap-2">
                <Network className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Understanding your map:</strong> The ontology shows main topics and relationships,
                  while the taxonomy organizes concepts hierarchically. Use this to understand the structure and themes in your dataset.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="card mt-8 bg-gray-900 border border-gray-800">
          <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">Best Practices</h3>
          <div className="space-y-3 text-sm text-gray-300">
            <div>
              <strong className="text-gray-200">When to generate:</strong>
              <p className="text-gray-400 mt-1 leading-6">
                Generate corpus maps after uploading a substantial number of documents (5-10+). More documents provide
                richer insights into your dataset structure.
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Re-generating maps:</strong>
              <p className="text-gray-400 mt-1 leading-6">
                Generate new corpus maps periodically as you add more documents. This keeps your visualization current
                with your evolving dataset.
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Using the insights:</strong>
              <p className="text-gray-400 mt-1 leading-6">
                Use corpus maps to understand what your documents contain before generating SOPs. This helps you craft
                better prompts and understand your knowledge base structure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorpusMapWorkflow;
